function requireSlot(slot, purpose) {
  if (!slot || typeof slot.id !== "string" || slot.purpose !== purpose) {
    throw new Error(`Project draft write is missing its ${purpose} upload slot.`);
  }

  return slot;
}

export function findDelegatedAssetSlot(receipt, purpose) {
  return requireSlot(
    receipt?.asset_slots?.find(slot => slot.purpose === purpose),
    purpose
  );
}

const projectAssetContentTypes = {
  gif: "image/gif",
  glb: "model/gltf-binary",
  jfif: "image/jpeg",
  jpeg: "image/jpeg",
  jpg: "image/jpeg",
  mp3: "audio/mpeg",
  mp4: "video/mp4",
  pdf: "application/pdf",
  png: "image/png",
  svg: "image/svg+xml",
  webp: "image/webp"
};

export function resolveProjectAssetContentType(file) {
  const supplied = typeof file?.type === "string" ? file.type.trim() : "";
  if (supplied) return supplied;
  const extension = typeof file?.name === "string" ? file.name.split(".").pop()?.toLowerCase() : "";
  return projectAssetContentTypes[extension] || "";
}

export async function beginProjectAssetWrite({ dashboardApiClient, projectKey, file, signal }) {
  const response = await dashboardApiClient.request(`projects/${projectKey}/asset-write-operations`, {
    method: "POST",
    body: JSON.stringify({
      filename: file.name,
      content_type: resolveProjectAssetContentType(file),
      size: file.size
    }),
    signal
  });

  return dashboardApiClient.handleResponse(response);
}

export async function cancelProjectAssetWrite({ dashboardApiClient, projectKey, operationId, signal }) {
  const response = await dashboardApiClient.request(`projects/${projectKey}/asset-write-operations/${operationId}`, {
    method: "DELETE",
    signal
  });

  if (!response.ok && response.status !== 404 && response.status !== 409) {
    await dashboardApiClient.handleResponse(response);
  }
}

export async function beginProjectDraftWrite({
  dashboardApiClient,
  projectKey,
  message,
  includeScreenshot,
  idempotencyKey,
  signal
}) {
  const response = await dashboardApiClient.request(`projects/${projectKey}/draft-write-operations`, {
    method: "POST",
    body: JSON.stringify({ message, include_screenshot: includeScreenshot }),
    headers: { "Idempotency-Key": idempotencyKey },
    signal
  });

  return dashboardApiClient.handleResponse(response);
}

export async function uploadDelegatedAsset({ uploadFile, file, folder, assetType, slot, signal }) {
  const uploaded = await uploadFile({
    file,
    folder,
    asset_type: assetType,
    abortSignal: signal,
    useWorkspaceAssets: true,
    delegatedAssetSlotId: slot.id,
    delegatedAssetPurpose: slot.purpose
  });

  if (!uploaded?.uuid) {
    throw new Error("Delegated Asset upload did not return a canonical Asset id.");
  }

  return uploaded.uuid;
}

export async function completeProjectDraftWrite({
  dashboardApiClient,
  projectKey,
  operationId,
  contentResourceId,
  screenshotResourceId,
  projectName,
  idempotencyKey,
  signal
}) {
  const response = await dashboardApiClient.request(
    `projects/${projectKey}/draft-write-operations/${operationId}/complete`,
    {
      method: "POST",
      body: JSON.stringify({
        content_resource_id: contentResourceId,
        screenshot_resource_id: screenshotResourceId,
        project_name: projectName
      }),
      headers: { "Idempotency-Key": idempotencyKey },
      signal
    }
  );

  return dashboardApiClient.handleResponse(response);
}

export async function cancelProjectDraftWrite({ dashboardApiClient, projectKey, operationId, signal }) {
  const response = await dashboardApiClient.request(`projects/${projectKey}/draft-write-operations/${operationId}`, {
    method: "DELETE",
    signal
  });

  if (!response.ok && response.status !== 404 && response.status !== 409) {
    await dashboardApiClient.handleResponse(response);
  }
}

export async function beginApplicationBuild({ dashboardApiClient, projectKey, command, idempotencyKey, signal }) {
  const response = await dashboardApiClient.request(`projects/${projectKey}/application/build-operations`, {
    method: "POST",
    body: JSON.stringify(command),
    headers: { "Idempotency-Key": idempotencyKey },
    signal
  });
  return dashboardApiClient.handleResponse(response);
}

export async function completeApplicationBuild({
  dashboardApiClient,
  projectKey,
  operationId,
  glbResourceId,
  thumbnailResourceId,
  signal
}) {
  const response = await dashboardApiClient.request(
    `projects/${projectKey}/application/build-operations/${operationId}/complete`,
    {
      method: "POST",
      body: JSON.stringify({ glb_resource_id: glbResourceId, thumbnail_resource_id: thumbnailResourceId }),
      signal
    }
  );
  return dashboardApiClient.handleResponse(response);
}

export async function cancelApplicationBuild({ dashboardApiClient, projectKey, operationId, signal }) {
  const response = await dashboardApiClient.request(
    `projects/${projectKey}/application/build-operations/${operationId}`,
    { method: "DELETE", signal }
  );
  if (!response.ok && response.status !== 404 && response.status !== 409)
    await dashboardApiClient.handleResponse(response);
}
